import { useState } from 'react';
import { Alert, Button, Select, Skeleton, Switch, Tag, Tooltip, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getEinvoiceReminderCandidates,
  getEinvoiceReminderCompany,
  saveEinvoiceReminderCompany,
} from '@/services/einvoiceReminders.service';
import EinvoiceMailPreview from './EinvoiceMailPreview';

const fmt = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '—');

/**
 * One company's monthly e-invoice reminder, next to its client contacts: the
 * switch (off by default), extra HLB people in copy, who would receive it and
 * the last sends. Managers and staff assigned to the company may change it.
 */
export default function EinvoiceReminderCompanyPanel({ companyId, companyName }) {
  const { t } = useTranslation('einvoiceReminders');
  const queryClient = useQueryClient();
  const [mailOpen, setMailOpen] = useState(false);
  const key = ['einvoice-reminder-company', companyId];
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => getEinvoiceReminderCompany(companyId),
    enabled: !!companyId,
    retry: false,
  });
  const { data: candidates } = useQuery({
    queryKey: ['einvoice-reminder-candidates'],
    queryFn: getEinvoiceReminderCandidates,
    enabled: !!data?.canEdit,
    staleTime: 10 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: (payload) => saveEinvoiceReminderCompany(companyId, payload),
    onSuccess: (d) => {
      queryClient.setQueryData(key, d);
      message.success(t('company.saved'));
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  // No access to this company's reminder (e.g. a read-only viewer): show nothing.
  if (isError) return null;
  if (isLoading || !data) return <Skeleton active paragraph={{ rows: 1 }} />;

  const options = (candidates || data.extraCc).map((u) => ({ value: u._id, label: `${u.name} (${u.email})` }));

  return (
    <div className="grid gap-2 rounded-lg border border-zinc-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Switch
            size="small"
            checked={data.enabled}
            disabled={!data.canEdit}
            loading={save.isPending}
            onChange={(enabled) => save.mutate({ enabled })}
          />
          <b className="text-sm">{t('company.title')}</b>
          {!data.globalEnabled && <Tag className="m-0">{t('company.globalOff')}</Tag>}
        </div>
        <Button size="small" shape="round" icon={<EyeOutlined />} onClick={() => setMailOpen(true)}>
          {t('company.previewMail')}
        </Button>
      </div>
      <div className="text-xs opacity-70">
        {t('company.next', { date: fmt(data.nextSendDay?.date), count: data.openCount })}
      </div>
      {!data.to.length && <Alert type="warning" showIcon className="!static" message={t('company.noTo')} />}
      <div className="grid gap-1 text-xs">
        <div>
          <span className="opacity-60">{t('company.to')}:</span> {data.to.join(', ') || '—'}
        </div>
        <div>
          <span className="opacity-60">{t('company.cc')}:</span> {data.cc.join(', ') || '—'}
        </div>
        <div>
          <span className="opacity-60">{t('company.language')}:</span> {t(`language.${data.language}`)}
        </div>
      </div>
      <div className="grid gap-1">
        <span className="text-xs opacity-60">{t('company.extraCc')}</span>
        <Select
          size="small"
          mode="multiple"
          showSearch
          optionFilterProp="label"
          disabled={!data.canEdit}
          value={data.extraCc.map((u) => u._id)}
          options={options}
          placeholder={t('company.extraCcPlaceholder')}
          onChange={(ids) => save.mutate({ extraCc: ids })}
          style={{ width: '100%' }}
        />
      </div>
      {data.log.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <span className="opacity-60">{t('company.log')}:</span>
          {data.log.map((l) => (
            <Tooltip key={l.sendDate} title={[l.to.join(', '), l.cc.join(', '), l.error].filter(Boolean).join(' · ')}>
              <Tag color={l.status === 'SENT' ? 'green' : l.status === 'FAILED' ? 'red' : 'default'} className="m-0">
                {fmt(l.sendDate)} · {t(`logStatus.${l.status}`)} ({l.invoiceCount})
              </Tag>
            </Tooltip>
          ))}
        </div>
      )}
      {mailOpen && <EinvoiceMailPreview companyId={companyId} companyName={companyName} onClose={() => setMailOpen(false)} />}
    </div>
  );
}
